import { ActionContext } from 'vuex';

import $rq from '@/api/api';
import type { RootState } from '@/interfaces/store';
import { Site } from '@/interfaces/common';

interface UnitsData {
    [key: string]: unknown;
}

interface TreatmentUnitsData {
    [key: string]: unknown;
}

interface SiteState {
    units: UnitsData | null;
    treatmentUnits: TreatmentUnitsData | null;
    // Allow other properties to be dynamically added by setSiteState
    [key: string]: unknown;
}

// Payloads for actions
interface GetUnitsPayload {
    siteId: string;
    lang: string;
}

interface GetTreatmentUnitsPayload {
    lang: string;
}

const defaultState = (): SiteState => ({
    units: null,
    treatmentUnits: null,
});

const mutations = {
    setSiteState(state: SiteState, site: Site) {
        Object.assign(state, site);
    },
    setUnits(state: SiteState, units: UnitsData) {
        state.units = units;
    },
    setTreatmentUnits(state: SiteState, treatmentUnits: TreatmentUnitsData) {
        state.treatmentUnits = treatmentUnits;
    },
    resetSiteState(state: SiteState) {
        // Reset to the initial structure defined by defaultState
        const initial = defaultState();
        Object.keys(state).forEach((key) => {
            if (!(key in initial)) {
                delete state[key];
            }
        });
        Object.assign(state, initial);
    },
};

const actions = {
    async getSiteInfo({ commit, dispatch }: ActionContext<SiteState, RootState>, siteId: string) {
        try {
            const { data } = await $rq.getSiteInfo(siteId);
            commit('setSiteState', data as Site);
        } catch (err) {
            dispatch('resetSiteInfo');
        }
    },
    async getUnits(
        { commit, dispatch }: ActionContext<SiteState, RootState>,
        content: GetUnitsPayload,
    ) {
        try {
            const { data } = await $rq.getUnits(content.siteId, content.lang);
            if (data && Object.keys(data).length) {
                commit('setUnits', data as UnitsData);
            } else {
                commit('setUnits', null);
            }
        } catch (err) {
            dispatch('resetSiteInfo');
        }
    },
    async getTreatmentUnits(
        { commit, dispatch }: ActionContext<SiteState, RootState>,
        content: GetTreatmentUnitsPayload,
    ) {
        try {
            const { data } = await $rq.getTreatmentUnits(content.lang);
            if (data && Object.keys(data).length) {
                commit('setTreatmentUnits', data as TreatmentUnitsData);
            } else {
                commit('setTreatmentUnits', null);
            }
        } catch (err) {
            dispatch('resetSiteInfo');
        }
    },
    resetSiteInfo({ commit }: ActionContext<SiteState, RootState>) {
        commit('resetSiteState');
    },
};

const getters = {
    site: (state: SiteState): SiteState => state, // Returns the whole site-specific state part
    units: (state: SiteState): UnitsData | null => state.units,
    treatmentProgramUnits: (state: SiteState): TreatmentUnitsData | null => state.treatmentUnits,
};

const state: SiteState = defaultState();

export default {
    state,
    getters,
    actions,
    mutations,
};
